import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import authService from '@/services/authService'
import { useAuth } from '@/hooks/useAuth'
import { consumePostLoginRedirect } from '@/utils/postLoginRedirect'
import logger from '@/utils/logger'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import type { ServerError } from '@/types'

function MagicLoginPage(): React.JSX.Element {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { setSession } = useAuth()
  const [error, setError] = useState<string>('')
  // A business (non-residential) customer used a magic link. Same block as the
  // login screen; shown its own message rather than a generic error (FEDEV-565).
  const [businessBlocked, setBusinessBlocked] = useState(false)
  const consumed = useRef(false)

  useEffect(() => {
    if (consumed.current) return
    consumed.current = true

    // A single random token (FEDEV-528). The link used to be a signed URL
    // carrying email, expires and signature; this page kept reading those and
    // so rejected every link the server now sends.
    const token = searchParams.get('token')

    if (!token) {
      setError('This login link is missing required information. Please request a new one.')
      return
    }

    authService
      .consumeMagicLink({ token })
      .then((result) => {
        setSession(result)
        logger.info('Magic-link login successful')
        // Return to where the customer was headed before signing in, e.g. a
        // one-off product link (DF-230); the magic link itself only carries a
        // token, so the intended path is read from storage set at trap time.
        navigate(consumePostLoginRedirect() ?? '/', { replace: true })
      })
      .catch((err: unknown) => {
        const serverError = err as ServerError
        logger.error('Magic-link login failed:', serverError)

        if ((serverError?.body as Record<string, unknown>)?.code === 'business_account_blocked') {
          setBusinessBlocked(true)
          return
        }

        const message =
          serverError?.body?.message ||
          serverError?.message ||
          'This login link is invalid or has expired.'
        setError(message)
      })
  }, [navigate, searchParams, setSession])

  if (businessBlocked) {
    return (
      <div className="auth-form-container">
        <div className="auth-form-card">
          <h2>Oops! This is a business account</h2>
          <p>
            It looks like you've tried to sign in with a business account. Our
            Customer Portal is currently only available for residential
            customers.
          </p>
          <p>
            If you need help with your account, please email us on{' '}
            <a href="mailto:milk@mcqueensdairies.co.uk">milk@mcqueensdairies.co.uk</a>{' '}
            and someone from the team will be in touch.
          </p>
          <p>
            <Link to="/login">Back to login</Link>
          </p>
        </div>
      </div>
    )
  }

  if (!error) {
    return <LoadingSpinner message="Logging you in..." />
  }

  return (
    <div className="auth-form-container">
      <div className="auth-form-card">
        <h2>Login link error</h2>
        <div className="alert alert-danger">{error}</div>
        <p>
          <Link to="/login">Back to login</Link>
        </p>
      </div>
    </div>
  )
}

export default MagicLoginPage
